import React, { useMemo, useCallback, ReactNode } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { OperationModeSchedule, OperationModesScheduleGroup } from '@features/shopautomation/types.ts';
import { StatusTag } from '@/components/shared/Tags.tsx';
import { isValidDayOfWeek } from '@/core/utils/daysOfWeek';
import { useSettingsStore } from '@core/store';
import { formatTime } from './scheduleDisplay';
import { useModeTagState } from '@core/hooks/useModeTagState.ts';

interface OperationModesScheduleCardProps {
    operationModesSchedule: OperationModesScheduleGroup;
    getActiveOnTranslation: (schedule: OperationModesScheduleGroup) => string;
    getSortedOperationModeSchedules: (schedules: OperationModeSchedule[]) => OperationModeSchedule[];
    onEdit: (schedule: OperationModesScheduleGroup) => void;
    onDelete: (schedule: OperationModesScheduleGroup) => void;
    schedulesEnabled?: boolean;
    isExpired?: boolean;
    scheduleLength?: number;
}

function buildScheduleModeTooltip(title: string, items: string[]): ReactNode {
    return (
        <Box>
            <Box className="schedule-mode__header">
                <Typography
                    fontSize={12}
                    fontFamily="var(--font-family-medium)"
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.8px' }}
                >
                    {title}
                </Typography>
            </Box>

            <Box className="schedule-mode__list">
                {items.map((item, i) => (
                    <Typography key={`${item}-${i}`} className="schedule-mode__list-item">
                        {item}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}

const OperationModesScheduleCard: React.FC<OperationModesScheduleCardProps> = ({
    operationModesSchedule,
    getActiveOnTranslation,
    getSortedOperationModeSchedules,
    onEdit,
    onDelete,
    schedulesEnabled = true,
    isExpired = false,
    scheduleLength
}) => {
    const { t } = useTranslation();
    const { isMobile } = useSettingsStore();
    const getModeTagState = useModeTagState();

    const sortedSchedules = useMemo(
        () => getSortedOperationModeSchedules(operationModesSchedule.operationModeSchedules),
        [operationModesSchedule.operationModeSchedules, getSortedOperationModeSchedules]
    );

    const cardTitle = useMemo(() => {
        const { activeOn, name } = operationModesSchedule;
        if (!activeOn) return '';

        if (activeOn === 'standard') return t('standard').toUpperCase();

        const trimmedName = name?.trim();
        if (trimmedName) return trimmedName.toUpperCase();

        const firstPart = activeOn.split(',')[0];

        if (/^\d{4}-\d{2}-\d{2}$/.test(firstPart)) {
            const dt = DateTime.fromISO(firstPart);
            const formatted = dt.isValid ? dt.toFormat('dd-MM-yy') : firstPart;
            return formatted;
        }

        if (isValidDayOfWeek(firstPart)) {
            const days = activeOn
                .split(',')
                .map((day) => getActiveOnTranslation({ ...operationModesSchedule, activeOn: day, name: '' }))
                .join(', ');
            return `${days.toUpperCase()} ${t('schedule').toUpperCase()}`;
        }

        return getActiveOnTranslation(operationModesSchedule).toUpperCase();
    }, [operationModesSchedule, getActiveOnTranslation, t]);

    const cardSubTitle = useMemo<{ lines: string[]; tooltipItems: string[] }>(() => {
        const { activeOn } = operationModesSchedule;
        const result = { lines: [] as string[], tooltipItems: [] as string[] };
        if (!activeOn) return result;

        if (activeOn === 'standard') {
            result.lines = [t('operationModeSchedules.defaultMode')];
            return result;
        }

        const activeOns = activeOn.split(',');
        const firstPart = activeOns[0];

        let items: string[] = [];
        let collapsedLabel = '';

        if (/^\d{4}-\d{2}-\d{2}$/.test(firstPart)) {
            const format = activeOns.length > 3 ? 'yyyy-MM-dd' : 'yy-MM-dd';
            items = activeOns.map((date) => {
                const dt = DateTime.fromISO(date);
                return dt.isValid ? dt.toFormat(format) : date;
            });
            collapsedLabel = `${activeOns.length} ${t('days')}`;
        } else if (isValidDayOfWeek(firstPart)) {
            items = activeOns.map((day) => t('operationModeSchedules.everyWeekDay', { day: t(day) }));
            collapsedLabel = t('operationModeSchedules.weekdays', { 0: activeOns.length });
        }

        if (items.length > 3) {
            result.lines = [collapsedLabel];
            result.tooltipItems = items;
        } else {
            result.lines = items;
        }

        return result;
    }, [operationModesSchedule, t]);

    const showMidnightBlock = useMemo(
        () => !sortedSchedules.some((item) => formatTime(item.from) === '00:00'),
        [sortedSchedules]
    );

    const handleEdit = useCallback(() => onEdit(operationModesSchedule), [onEdit, operationModesSchedule]);
    const handleDelete = useCallback(() => onDelete(operationModesSchedule), [onDelete, operationModesSchedule]);
    const canDelete = useMemo(
        () => operationModesSchedule.activeOn !== 'standard' || scheduleLength === 1,
        [operationModesSchedule.activeOn, scheduleLength]
    );

    const desktopActionButtons = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <IconButton onClick={handleEdit} size="small" disabled={!schedulesEnabled}>
                <i className="bi bi-pencil" style={{ fontSize: '14px' }} />
            </IconButton>
            <IconButton
                onClick={canDelete ? handleDelete : undefined}
                size="small"
                disabled={!canDelete || !schedulesEnabled}
                sx={{ visibility: canDelete ? 'visible' : 'hidden' }}
            >
                <i className="bi bi-trash3" style={{ fontSize: '14px' }} />
            </IconButton>
        </Box>
    );

    const mobileActionButtons = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <IconButton onClick={handleEdit} size="small" disabled={!schedulesEnabled} sx={{ p: '4px 0 !important' }}>
                <i className="bi bi-pencil" style={{ fontSize: '14px' }} />
            </IconButton>
            {canDelete && (
                <IconButton
                    onClick={handleDelete}
                    size="small"
                    disabled={!schedulesEnabled}
                    sx={{ p: '4px 0 !important' }}
                >
                    <i className="bi bi-trash3" style={{ fontSize: '14px' }} />
                </IconButton>
            )}
        </Box>
    );

    return (
        <Box
            className={isMobile ? 'schedule-card--mobile' : undefined}
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'flex-start',
                gap: isMobile ? 1.5 : 2,
                py: isMobile ? undefined : 2.5,
                opacity: schedulesEnabled ? 1 : 0.6,
                pointerEvents: schedulesEnabled ? 'auto' : 'none'
            }}
        >
            <Box
                sx={
                    isMobile
                        ? {
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 1,
                              width: '100%'
                          }
                        : {
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              width: 200,
                              flexShrink: 0,
                              pt: '4px'
                          }
                }
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#000',
                            fontFamily: 'Wanzl_Etelka Light',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            letterSpacing: '0.8px',
                            textTransform: 'uppercase',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            display: 'block'
                        }}
                    >
                        {cardTitle}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box>
                            {cardSubTitle.lines.map((line, i) => (
                                <Typography
                                    key={`${line}-${i}`}
                                    sx={{
                                        color: '#878787',
                                        fontFamily: 'Wanzl_Etelka Light',
                                        fontSize: '10px',
                                        fontWeight: 400,
                                        letterSpacing: '0.8px',
                                        lineHeight: '20px',
                                        display: 'block',
                                        textTransform: 'lowercase'
                                    }}
                                >
                                    {line}
                                </Typography>
                            ))}
                        </Box>

                        {cardSubTitle.tooltipItems.length > 0 && (
                            <Tooltip
                                title={buildScheduleModeTooltip(cardTitle, cardSubTitle.tooltipItems)}
                                placement={isMobile ? 'bottom' : 'right'}
                                arrow
                                slotProps={{
                                    tooltip: { className: 'schedule-mode__tooltip' },
                                    popper: {
                                        popperOptions: { strategy: 'fixed' as const },
                                        modifiers: isMobile
                                            ? [
                                                  { name: 'offset', options: { offset: [0, 4] } },
                                                  {
                                                      name: 'preventOverflow',
                                                      options: { boundary: 'viewport', padding: 8 }
                                                  },
                                                  { name: 'flip', options: { fallbackPlacements: ['top'] } }
                                              ]
                                            : [
                                                  { name: 'offset', options: { offset: [0, -15] } },
                                                  {
                                                      name: 'preventOverflow',
                                                      options: { boundary: 'viewport', padding: 8, tether: false }
                                                  },
                                                  {
                                                      name: 'flip',
                                                      options: { fallbackPlacements: ['right-end', 'top', 'bottom'] }
                                                  }
                                              ]
                                    }
                                }}
                            >
                                <IconButton
                                    size="small"
                                    sx={{
                                        placeSelf: 'center',
                                        '& i': { fontSize: '12px !important', color: 'var(--gray-medium)' }
                                    }}
                                >
                                    <i className="bi bi-info-circle" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {isMobile && mobileActionButtons}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'flex-start',
                    minWidth: 0,
                    opacity: isExpired && schedulesEnabled ? 0.6 : 1
                }}
            >
                {showMidnightBlock && (
                    <Box sx={{ flex: 0.2, minWidth: 0 }}>
                        <Box
                            sx={{
                                bgcolor: '#ebebeb',
                                borderRadius: '3px',
                                px: 1,
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
                                …
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.25 }}>
                            <Typography variant="caption" sx={{ fontSize: '10px', color: 'var(--gray-medium)' }}>
                                00:00
                            </Typography>
                        </Box>
                    </Box>
                )}

                {sortedSchedules.map((operationMode, index) => {
                    const isLast = index === sortedSchedules.length - 1;
                    const isFirst = index === 0;
                    const tagState = getModeTagState(operationMode.operationMode);

                    return (
                        <Box
                            key={operationMode.placeholderId || operationMode.id || index}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            <StatusTag
                                state={tagState}
                                label={operationMode.operationMode ?? '—'}
                                style={{
                                    width: '100%',
                                    borderRadius: '3px',
                                    justifyContent: 'flex-start'
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                                {(isFirst && !showMidnightBlock) || !isFirst ? (
                                    <Typography
                                        variant="caption"
                                        sx={{ fontSize: '10px', color: 'var(--gray-medium)' }}
                                    >
                                        {formatTime(operationMode.from)}
                                    </Typography>
                                ) : (
                                    <span />
                                )}
                                {isLast && (
                                    <Typography
                                        variant="caption"
                                        sx={{ fontSize: '10px', color: 'var(--gray-medium)' }}
                                    >
                                        24:00
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {!isMobile && desktopActionButtons}
        </Box>
    );
};

export default OperationModesScheduleCard;
